import { CheckCircle2, XCircle, ArrowDownRight } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { useDashboardEntityMetrics } from '@/hooks/useDashboard';
import { cn } from '@/lib/cn';

function formatEntityName(entityType: string): string {
  return entityType
    .split('-')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

export function EntityMetricsTable() {
  const { data, isLoading, error } = useDashboardEntityMetrics();

  if (isLoading) {
    return (
      <Card>
        <CardContent className="flex h-[300px] items-center justify-center p-4">
          <LoadingSpinner size="md" />
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent className="flex h-[300px] items-center justify-center p-4">
          <p className="text-xs text-red-400">Failed to load entity metrics</p>
        </CardContent>
      </Card>
    );
  }

  if (!data?.entities || data.entities.length === 0) {
    return (
      <Card>
        <CardContent className="flex h-[300px] items-center justify-center p-4">
          <p className="text-xs text-gray-500">No entity metrics available</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent className="p-4">
        <div className="mb-4">
          <p className="text-[10px] font-medium uppercase tracking-wider text-emerald-500">// Entity_Metrics</p>
          <h3 className="mt-1 text-sm font-semibold uppercase tracking-wider text-white">Processing by Entity (24H)</h3>
        </div>

        {/* Table Header */}
        <div className="grid grid-cols-6 gap-4 border-b border-white/10 pb-2 text-[10px] font-medium uppercase tracking-wider text-gray-500">
          <span>Entity</span>
          <span className="text-right">Succeeded</span>
          <span className="text-right">Failed</span>
          <span className="text-right">Success Rate</span>
          <span className="text-right">Avg Latency</span>
          <span className="text-center">Status</span>
        </div>

        {/* Table Body */}
        <div className="divide-y divide-white/5">
          {data.entities.map((entity) => {
            const hasActivity = entity.succeeded24h > 0 || entity.failed24h > 0;
            const statusColor =
              entity.successRate >= 99
                ? 'text-emerald-400'
                : entity.successRate >= 95
                  ? 'text-amber-400'
                  : 'text-red-400';

            return (
              <div
                key={entity.entityType}
                className="grid grid-cols-6 gap-4 py-3 text-xs transition-colors hover:bg-white/[0.02]"
              >
                <div className="flex items-center gap-2">
                  <ArrowDownRight className="h-3 w-3 text-gray-600" />
                  <span className="font-medium text-white">{formatEntityName(entity.entityType)}</span>
                </div>
                <span className={cn('text-right', hasActivity ? 'text-emerald-400' : 'text-gray-500')}>
                  {entity.succeeded24h.toLocaleString()}
                </span>
                <span className={cn('text-right', entity.failed24h > 0 ? 'text-red-400' : 'text-gray-500')}>
                  {entity.failed24h.toLocaleString()}
                </span>
                <span className={cn('text-right font-medium', statusColor)}>
                  {hasActivity ? `${entity.successRate}%` : '—'}
                </span>
                <span className="text-right text-gray-400">
                  {hasActivity ? `${entity.avgLatencyMs}ms` : '—'}
                </span>
                <div className="flex justify-center">
                  {hasActivity ? (
                    entity.successRate >= 99 ? (
                      <CheckCircle2 className="h-4 w-4 text-emerald-400" />
                    ) : entity.successRate >= 95 ? (
                      <CheckCircle2 className="h-4 w-4 text-amber-400" />
                    ) : (
                      <XCircle className="h-4 w-4 text-red-400" />
                    )
                  ) : (
                    <span className="text-[10px] text-gray-500">No Activity</span>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Totals Footer */}
        {data.totals && (
          <div className="mt-4 grid grid-cols-2 gap-4 border-t border-white/10 pt-4">
            <div className="flex items-center justify-between rounded bg-emerald-500/10 px-3 py-2">
              <span className="text-[10px] uppercase text-gray-400">Total Succeeded</span>
              <span className="text-lg font-bold text-emerald-400">
                {data.totals.downstream.succeeded.toLocaleString()}
              </span>
            </div>
            <div className="flex items-center justify-between rounded bg-red-500/10 px-3 py-2">
              <span className="text-[10px] uppercase text-gray-400">Total Failed</span>
              <span className={cn('text-lg font-bold', data.totals.downstream.failed > 0 ? 'text-red-400' : 'text-white')}>
                {data.totals.downstream.failed.toLocaleString()}
              </span>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
